export type ReservedMember = {
  memberId: string
  nickname: string
  avatarUrl?: string
}

export type ReservationRecord = {
  id: string
  tableId: string
  tableName?: string
  tableNo?: string
  status: string
}

export type QueueRecord = {
  id: string
  tableId?: string
  rankNo: number
  status: string
}

export type ReservationTable = {
  id: string
  tableNo: string
  name?: string
  status: string
  statusText?: string
  enabled?: number
  maxPlayers: number
  occupiedSeats: number
  canReserve: boolean
  updatedAt?: string
  reservedMembers?: ReservedMember[]
  queue?: QueueRecord[]
}

export type ReservationDesk = {
  store?: { id: string; name: string }
  tables: ReservationTable[]
  myReservation?: ReservationRecord | null
  myQueue?: QueueRecord | null
}

export type ReservationMemberProfile = {
  memberId: string
  nickname: string
  avatarUrl?: string
  areaId: string
  areaName: string
  levelCode: number
  levelName: string
  badgeColor?: string
  pointsBalance: number
  totalDepositPoints: number
}

export type DecoratedReservationTable = ReservationTable & {
  areaName: string
  remaining: number
  unavailable: boolean
  hasCapacity: boolean
  queueable: boolean
  queueCount: number
}

export function reservationAreaName(table?: Pick<ReservationTable, 'name' | 'tableNo'> | null) {
  const raw = String(table?.name || table?.tableNo || '活动区域')
  const normalized = raw.endsWith('桌') ? `${raw.slice(0, -1)}区` : raw
  return normalized.replace(/德州/g, '娱乐')
}

export function decorateReservationTables(tables?: ReservationTable[]) {
  return (tables || []).map<DecoratedReservationTable>((table) => {
    const maxPlayers = Number(table.maxPlayers || 0)
    const occupiedSeats = Number(table.occupiedSeats ?? table.reservedMembers?.length ?? 0)
    const remaining = Math.max(0, maxPlayers - occupiedSeats)
    const unavailable = table.enabled === 0 || ['CLOSED', 'ENDED'].includes(table.status)
    const hasCapacity = !unavailable && table.canReserve && remaining > 0
    return {
      ...table,
      occupiedSeats,
      areaName: reservationAreaName(table),
      remaining,
      unavailable,
      hasCapacity,
      queueable: !unavailable && !hasCapacity,
      queueCount: table.queue?.length || 0
    }
  })
}

export function preferredReservationTableId(
  tables: DecoratedReservationTable[],
  currentId: string,
  reservation?: ReservationRecord | null,
  queue?: QueueRecord | null
) {
  const preferred = reservation?.tableId || queue?.tableId
  if (preferred && tables.some((table) => table.id === preferred)) return preferred
  if (currentId && tables.some((table) => table.id === currentId)) return currentId
  return tables.find((table) => table.hasCapacity)?.id || tables[0]?.id || ''
}
